import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import { ref, provide, onMounted, computed, watch, onBeforeUnmount } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useDeviceStore } from "@/stores/deviceStore";
import { useUserStore } from "@/stores/userStore";
import { useLineStore } from "@/stores/lineStore";
import deviceHelper from "@/utils/deviceInfo";
import ErrorModal from "@/components/shared/modal/base/ErrorModal.vue";
import ToastMessage from "@/components/shared/ToastMessage.vue";
import Modal from "@/components/shared/modal/base/Modal.vue";
import NoticeModal from "@/components/shared/modal/base/NoticeModal.vue";
import LoadingView from "@/components/shared/LoadingView.vue";
import {
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  LayoutMode,
  RouterName,
  PROVIDE_KEY_CURRENT_LOCATION,
  PROVIDE_KEY_GET_CURRENT_LOCATION,
  Location,
  AUTO_LOGIN,
  HAS_READ_PERMISSIONS,
  TermsTypeCode,
  ErrorTypeCode,
  PROVIDE_KEY_HANDLE_CHECK_PASSWORD_EXPIRED,
  ChangePasswordExpiredSourceType,
  Block,
  PROVIDE_KEY_IS_LOCATION_PERMISSION_DENIED,
  PROVIDE_KEY_RESIZE_WEBVIEW,
} from "./types";
import { INoticeInfoData } from "./domain/entities/bbs";
import { useServiceStore } from "./stores/serviceStore";
import { setSerivceCode, setServiceUid } from "./types";
import dayjs from "dayjs";
import CompanyInfo from "./components/mypage/CompanyInfo.vue";
import { ITermsInfo } from "./domain/entities/serviceInfo/termsInfo";
import useResizeWebviewWithSafeArea from "./use/useResizeWebviewWithSafeArea";
import useGetAccessToken from "./use/useGetAccessToken";
import useSetColor from "./use/useSetColor";
import AppPermissionsModal from "./components/shared/modal/AppPermissionsModal.vue";
import { useGetUserInfo } from "./composables/user/useGetUserInfo";
import { useCheckPasswordExpired } from "./composables/user/useCheckPasswordExpired";
import { useUseBlockChecker } from "./composables/block/useUseBlockChecker";
import ZoomControl from "./components/shared/ZoomControl.vue";
import { useSystemStore } from "./stores/systemStore";


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props, { expose: __expose }) {
  __expose();

_useCssVars(_ctx => ({
  "7ba5bd90-contentMaxWidth": (contentMaxWidth.value),
  "7ba5bd90-floatingRightOffsetPx": (floatingRightOffsetPx.value)
}))

const router = useRouter();
const route = useRoute();

const isAndroid = deviceHelper.isAndroid();
const isiOS = deviceHelper.isiOS();
const isNativeApp = deviceHelper.isNativeApp();
const isMobile = deviceHelper.isMobile();
const isReadyForService = ref(false);
const updateKey = ref(0);

const serviceStore = useServiceStore();
const systemStore = useSystemStore();
const deviceStore = useDeviceStore();
const userStore = useUserStore();
const lineStore = useLineStore();

const termsList = ref<ITermsInfo[]>([]);
const layoutMode = computed(() =>
  isMobile ? LayoutMode.FULL : route.meta.pcLayoutMode
);
const contentMaxWidth = computed(() =>
  isMobile || layoutMode.value === LayoutMode.FULL ? "100%" : "475px"
);
const logoPath = computed(() => serviceStore.serviceInfo?.logoPath);

const targetPermissions = computed(() => {
  const serviceInfo = serviceStore.serviceInfo;
  if (isAndroid) {
    return serviceInfo?.appPermissions?.android ?? [];
  } else if (isiOS) {
    return serviceInfo?.appPermissions?.ios ?? [];
  } else {
    return [];
  }
});

const whiteListDomains = computed(
  () => serviceStore.serviceInfo?.whiteListDomains
);

const passwordExpiredAlramIntervalDay = computed(
  () => serviceStore.serviceInfo?.passwordChangeAlarmIntervalDay ?? 0
);

const canExtendPasswordExpiration = computed(
  () =>
    (serviceStore.serviceInfo?.passwordNextChangeLimit ?? 0) >
    (userStore.userInfo?.passwordReuseCount ?? 0)
);

/** 이용약관 새창에서 열기 */
const showTermsOnNewWindow = (termsTypeCode: TermsTypeCode) => {
  const selectedTerms = termsList.value?.find(
    (terms) => terms.code === termsTypeCode
  );
  window.open(selectedTerms?.url ?? "", "_blank");
};

const serviceCode = route.params.serviceCode as string;
setSerivceCode(serviceCode);

const activateServiceStatus = () => {
  isReadyForService.value = true;
};

// 안드로이드 인터페이스 존재 확인
const isAndroidFunctionAvailable = (functionName: string) => {
  return typeof window.android?.[functionName] === "function";
};

/** 첫 진입 화면 */
const firstEnterPage = computed(() => {
  if (!serviceStore.serviceInfo) {
    return "";
  }

  const { isPublicLineApi, isPublicTalkApi } = serviceStore.serviceInfo;
  return isPublicLineApi || isPublicTalkApi
    ? RouterName.MainLogout
    : RouterName.Login;
});

// 안드로이드 뒤로가기 버튼 처리
window.onBackPressed = () => {
  if (route.meta.onBackHandlerList.length) {
    const onBackHandler =
      route.meta.onBackHandlerList[route.meta.onBackHandlerList.length - 1];
    if (typeof onBackHandler === "function") {
      onBackHandler();
      return;
    }
  }

  const hasNoHistory = window.history.length <= 1;
  if (
    hasNoHistory ||
    route.name === firstEnterPage.value ||
    route.name === RouterName.MainLogin
  ) {
    askFinishApp();
    return;
  }

  route.meta.willGoBack = true;
  router.back();
};

const setStatusBarHeight = (height: number) => {
  document.documentElement.style.setProperty(
    "--statusbar-height",
    `${height}px`
  );
  // updateKey.value += 1;
};

const setBottomNavBarHeight = (height: number) => {
  if (isAndroid) {
    document.documentElement.style.setProperty(
      "--bottom-navbar-height",
      `${height}px`
    );
  } else if (isiOS) {
    document.documentElement.style.setProperty("--ios-bottom-padding", "20px");
  }
  // updateKey.value += 1;
};

const setNotificationAllowStatus = (isEnabled: boolean) => {
  const isAllowed =
    typeof isEnabled === "string" ? isEnabled === "true" : isEnabled;
  userStore.updateNotificationAllowStatus(isAllowed);
};

const updateNotificationAllowStatus = () => {
  if (isAndroid && isAndroidFunctionAvailable("isNotificationEnabled")) {
    const isEnabled = window.android?.isNotificationEnabled();
    setNotificationAllowStatus(isEnabled === "true");
  } else if (isiOS) {
    // iOS 에서 Message를 받으면 웹의 setNotificationAllowStatus 함수 호출
    window.webkit?.messageHandlers.isNotificationEnabled.postMessage("");
  }
};

const onKeyboardChanged = (show: boolean, keyboardHeight: number) => {
  document.documentElement.style.setProperty(
    "--keyboard-height",
    `${keyboardHeight}px`
  );
  deviceStore.setKeyboardSatus(show);
};

// 외부 브라우저로 링크 열기
const openLinkWithExternalBrowser = (url: string) => {
  if (isAndroid && isAndroidFunctionAvailable("openLinkWithExternalBrowser")) {
    window.android?.openLinkWithExternalBrowser(url);
  } else if (isiOS) {
    window.webkit?.messageHandlers?.openLinkWithExternalBrowser?.postMessage(
      url
    );
  }
};

const setPushToken = (token: string) => {
  userStore.setPushToken(token);
};

const updatePushToken = () => {
  if (isAndroid && isAndroidFunctionAvailable("getPushToken")) {
    const token = window.android?.getPushToken();
    setPushToken(token);
  } else if (isiOS) {
    // iOS 에서 Message를 받으면 웹의 setPushToken 함수 호출
    window.webkit?.messageHandlers?.getPushToken?.postMessage("");
  }
};

/** 알림 설정 권한이 결정되었는지 여부 처리 */
const isNotificationNotDetermined = ref(false);
const setNotificationPermissionDetermined = (
  isDetermined: string | boolean
) => {
  isNotificationNotDetermined.value =
    typeof isDetermined === "string" ? isDetermined !== "true" : !isDetermined;

  handleAppPermissionsModal();
};

const handleAppPermissionsModal = () => {
  /*
   * 안드로이드의 경우 OS 13 (SDK 33) 미만이면 isNotificationNotDetermined.value 가 항상 false 이기 때문에
   * hasAutoLoginValue 를 가지고 최초 설치 여부 판단
   */
  let shouldShow = false;
  if (isAndroid && androidSdkVersion.value < 33) {
    shouldShow = !window.localStorage.getItem(AUTO_LOGIN)?.length;
  } else {
    shouldShow = isNotificationNotDetermined.value;
  }

  if (shouldShow) {
    toggleShowAppPermissionsModal(true);
    window.localStorage.setItem(HAS_READ_PERMISSIONS, "true");
  } else {
    // 이미 앱을 설치한 사람인 경우
    window.localStorage.setItem(HAS_READ_PERMISSIONS, "true");
  }
};

const updateIsNotificationDetermined = () => {
  if (
    isAndroid &&
    isAndroidFunctionAvailable("isNotificationPermissionDetermined")
  ) {
    const isDetermined = window.android?.isNotificationPermissionDetermined();
    setNotificationPermissionDetermined(isDetermined);
  } else if (isiOS) {
    // iOS 에서 Message를 받으면 웹의 setNotificationPermissionDetermined 함수 호출
    window.webkit?.messageHandlers?.isNotificationPermissionDetermined?.postMessage(
      ""
    );
  }
};

/** 안드로이드 sdk 버전 가져오기 */
const androidSdkVersion = ref(-1);
const updateAndroidSdkVersion = () => {
  if (isAndroid && isAndroidFunctionAvailable("getAndroidSdkVersion")) {
    androidSdkVersion.value = Number(
      window.android?.getAndroidSdkVersion() ?? -1
    );
  }
};

const updatePermissionSettingsIfNeeded = () => {
  const hasReadPermissions =
    window.localStorage.getItem(HAS_READ_PERMISSIONS) === "true";
  if (!hasReadPermissions) {
    updateAndroidSdkVersion();
    updateIsNotificationDetermined();
  }
};

const onAppStatusChanged = (isActive: boolean) => {
  deviceStore.updateApplicationActiveStatus(isActive);
  if (!isActive) return;
  if (
    route.name === RouterName.BookSuccess ||
    route.name === RouterName.BookFail
  )
    return;

  updateLineInfos();
  updatePushToken();
  updateNotificationAllowStatus();
};

const getSerivceInfo = async () => {
  const serviceInfoResult = await serviceStore.getServiceInfo();

  if (!serviceInfoResult.isSuccess) {
    return false;
  }

  const serviceInfo = serviceInfoResult.data;
  setServiceUid(serviceStore.serviceUid);
  if (serviceInfo?.color) {
    useSetColor(serviceInfo.color);
  }
  termsList.value = serviceStore.termsList;
  return true;
};

const updateLineInfos = async () => {
  const accessToken = useGetAccessToken();

  const serviceInfo = serviceStore.serviceInfo;
  const isPublicLineApi = serviceInfo?.isPublicLineApi;
  const isPublicTalkApi = serviceInfo?.isPublicTalkApi;
  const sortOrder = serviceInfo?.lineSortOrder;

  if (isPublicLineApi || accessToken) {
    const [lineInfosResult, lineInfosLiteResult] = await Promise.all([
      lineStore.getAllLineInfos(sortOrder),
      lineStore.getAllLineInfosLite(sortOrder),
    ]);

    // 둘 중 하나라도 실패하면 에러 처리하고 진행 중단
    if (!lineInfosResult.isSuccess || !lineInfosLiteResult.isSuccess) {
      // 우선순위: 기본 lineInfos 에러 > lite 에러
      const primaryError = !lineInfosResult.isSuccess
        ? lineInfosResult
        : lineInfosLiteResult;

      showErrorModal(
        "노선목록 오류",
        primaryError.errorMessage,
        primaryError.resultCode,
        primaryError.errorTime
      );

      return; // 여기서 함수 종료 - 더 이상 진행하지 않음
    }
  }

  if (
    !isPublicTalkApi &&
    !accessToken &&
    (route.name === RouterName.MainLogout || route.name === RouterName.Login)
  ) {
    router.replace({
      name: RouterName.Login,
      params: { isEntryPage: "true" },
    });
  }
};

const getUserInfo = async () => {
  const userInfoResult = await useGetUserInfo();
  if (userInfoResult.isSuccess) return;
  // Do nothing
  if (userInfoResult.errorType === ErrorTypeCode.NOT_FOUND_ACCESS_TOKEN) return;

  showErrorModal(
    "사용자 정보 오류",
    userInfoResult.errorMessage,
    userInfoResult.resultCode,
    userInfoResult.errorTime
  );
};

/** 캠퍼스 목록 조회 */
const getCampusList = async () => {
  const result = await systemStore.getCustomCodes("CPS");

  if (!result.isSuccess) {
    showErrorModal(
      "캠퍼스 조회 오류",
      result.errorMessage,
      result.resultCode,
      result.errorTime
    );
  }

  const campusList = result.data?.customCodes ?? [];
  systemStore.updateCampusList(campusList);
  systemStore.updateCampusCoordinateList(campusList);
};

// safe 영역에 따라 웹뷰 크기 재설정
const resizeWebviewWithSafeArea = (
  withSafeArea: boolean,
  delayMillis?: number
) => {
  if (isAndroid) {
    window.android.resizeWebviewWithSafeArea(withSafeArea, delayMillis ?? 0);
  } else if (isiOS) {
    window.webkit.messageHandlers.resizeWebviewWithSafeArea.postMessage(
      `${withSafeArea ? "true" : "false"},${delayMillis ?? 0}`
    );
  }
};
provide(PROVIDE_KEY_RESIZE_WEBVIEW, resizeWebviewWithSafeArea);

resizeWebviewWithSafeArea(false);

// 모바일 웹으로 접근시 화면 높이 처리
const setViewportHeight = () => {
  if (isNativeApp) {
    document.documentElement.style.setProperty("--vh", "1vh");
  } else {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  }
};

setViewportHeight();

const onChangedWebviewSize = () => {
  setViewportHeight();
};

window.setStatusBarHeight = setStatusBarHeight;
window.setBottomNavBarHeight = setBottomNavBarHeight;
window.setNotificationAllowStatus = setNotificationAllowStatus;
window.onKeyboardChanged = onKeyboardChanged;
window.setPushToken = setPushToken;
window.setNotificationPermissionDetermined =
  setNotificationPermissionDetermined;
window.onAppStatusChanged = onAppStatusChanged;
window.openLinkWithExternalBrowser = openLinkWithExternalBrowser;
window.onChangedWebviewSize = onChangedWebviewSize;

if (
  isAndroid &&
  isAndroidFunctionAvailable("getStatusBarHeight") &&
  isAndroidFunctionAvailable("getBottomNavigationBarHeight")
) {
  const statusBarHeight = window.android?.getStatusBarHeight() || 0;
  setStatusBarHeight(statusBarHeight);

  const bottomNavHeight = window.android?.getBottomNavigationBarHeight() || 0;
  setBottomNavBarHeight(bottomNavHeight);
} else if (isiOS) {
  window.webkit?.messageHandlers.getStatusBarHeight.postMessage("");
  window.webkit?.messageHandlers.getBottomNavigationBarHeight.postMessage("");
}

useResizeWebviewWithSafeArea(false);

/** 로딩 이미지 처리 */
const shouldShowLoadingImage = ref(false);
const showLoadingImage = () => {
  shouldShowLoadingImage.value = true;
};
provide(PROVIDE_KEY_SHOW_LOADING_IMAGE, showLoadingImage);

const removeLoadingImage = () => {
  shouldShowLoadingImage.value = false;
};
provide(PROVIDE_KEY_REMOVE_LOADING_IMAGE, removeLoadingImage);

/** 현재 위치 처리 */
const currentLocation = ref<Location>();
provide(PROVIDE_KEY_CURRENT_LOCATION, currentLocation);

const getCurrentLocation = () => {
  // 위치 권한 거부 여부 초기화
  updateIsLocationPermissionDenied(false);

  if (isAndroid && isAndroidFunctionAvailable("getLocationInfo")) {
    window.android?.getLocationInfo();
  } else if (isiOS) {
    window.webkit?.messageHandlers.getLocationInfo.postMessage("");
  } else {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        currentLocation.value = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
      },
      (error) => {
        currentLocation.value = { latitude: -1, longitude: -1 };
      }
    );
  }
};
provide(PROVIDE_KEY_GET_CURRENT_LOCATION, getCurrentLocation);

// 네이티브 앱에서 호출하는 함수
const setCurrentLocation = (
  latitude: string | number,
  longitude: string | number
) => {
  currentLocation.value = {
    latitude: Number(latitude),
    longitude: Number(longitude),
  };
};
window.setCurrentLocation = setCurrentLocation;

/** 위치 권한 거부 여부 */
const isLocationPermissionDenied = ref(false);
const updateIsLocationPermissionDenied = (isDenied: boolean) => {
  isLocationPermissionDenied.value = isDenied;
};
provide(PROVIDE_KEY_IS_LOCATION_PERMISSION_DENIED, isLocationPermissionDenied);

const hasNoLocationPermission = () => {
  updateIsLocationPermissionDenied(true);
};
window.hasNoLocationPermission = hasNoLocationPermission;

/** 에러 모달 처리 */
const shouldShowErrorModal = ref(false);
const errorTitle = ref("오류");
const errorMessage = ref("");
const errorCode = ref(0);
const errorTime = ref("");
const errorCallback = ref<() => void>();
const showErrorModal = (
  title = "오류",
  message = "",
  code = 0,
  time?: string,
  callback?: () => void
) => {
  shouldShowErrorModal.value = true;
  errorTitle.value = title;
  errorMessage.value = message;
  errorCode.value = code;
  errorTime.value = time ? dayjs(time).format("YYYY-MM-DD HH:mm:ss") : "";
  if (callback) {
    errorCallback.value = callback;
  }
};
provide(PROVIDE_KEY_SHOW_ERROR_MODAL, showErrorModal);

const hideErrorModal = () => {
  shouldShowErrorModal.value = false;
  if (errorCallback.value) {
    errorCallback.value();
    errorCallback.value = undefined;
  }
};

/** 공지 모달 처리 */
const shouldShowNotice = ref(false);
const popupNoticeInfos = ref<INoticeInfoData[]>([]);
const isColsedNoticeModal = ref(false);
const showNoticeModal = (noticeInfos: INoticeInfoData[]) => {
  popupNoticeInfos.value = noticeInfos;
  if (noticeInfos.length) {
    shouldShowNotice.value = true;
    return;
  }
  onCloseNoticeModal();
};
const onCloseNoticeModal = () => {
  shouldShowNotice.value = false;
  isColsedNoticeModal.value = true;
};

/** 토스트 메시지 처리 */
const shouldShowToast = ref(false);
const toastMessage = ref("");
const toastDuration = ref(3000);
const toastDelay = ref(0);
const toastUpdateKey = ref(0);
const showToastMessage = (
  message: string,
  durationMillis = 3000,
  delayMillis = 300
) => {
  shouldShowToast.value = false;
  toastMessage.value = message;
  toastDuration.value = durationMillis;
  toastDelay.value = delayMillis;
  shouldShowToast.value = true;
  toastUpdateKey.value += 1;
};
provide(PROVIDE_KEY_SHOW_TOAST_MESSAGE, showToastMessage);

/** 앱 종료 모달 처리 */
const shouldShowFinishAppModal = ref(false);
const askFinishApp = () => {
  shouldShowFinishAppModal.value = true;
};
window.askFinishApp = askFinishApp;

const finishApp = () => {
  if (isAndroid && isAndroidFunctionAvailable("finishApp")) {
    window.android?.finishApp();
  }
};

/** 앱 접근 권한 모달 처리  */
const shouldShowAppPermissionsModal = ref(false);
const toggleShowAppPermissionsModal = (show: boolean) => {
  if (!targetPermissions.value.length) return;
  shouldShowAppPermissionsModal.value = show;
};

const onClosedAppPermissionsModal = () => {
  if (
    isAndroid &&
    isAndroidFunctionAvailable("onClosedPermissionsDescription")
  ) {
    window.android?.onClosedPermissionsDescription();
  } else if (isiOS) {
    window.webkit?.messageHandlers?.onClosedPermissionsDescription?.postMessage(
      ""
    );
  }

  toggleShowAppPermissionsModal(false);
};

/** 안드로이드 외부링크 백버튼 처리 */
// window.android?.setBaseUrl();
// window.android?.setNoAppRedirectUrl();

/** 탑승에 대한 제한체크를 위한 인터벌 설정과 해제 */
const { removeIntervalUseBlockChecker: removeBoardIntervalBlockChcker } =
  useUseBlockChecker(Block.BOARD);

const { removeIntervalUseBlockChecker: removeBookIntervalBlockChcker } =
  useUseBlockChecker(Block.BOOK);

/** 외부 브라우저로 보여주는 링크 목록 처리 */
const setExternalLinks = () => {
  const externalLinks = [...(whiteListDomains.value ?? [])];

  if (isAndroid && isAndroidFunctionAvailable("setExternalLinks")) {
    window.android?.setExternalLinks(JSON.stringify(externalLinks));
  } else if (isiOS) {
    window.webkit?.messageHandlers?.setExternalLinks?.postMessage(
      externalLinks
    );
  }
};

const updateSettings = () => {
  updatePermissionSettingsIfNeeded();
  updatePushToken();
  updateNotificationAllowStatus();
  setExternalLinks();
};

/** 비밀번호 만료 전 안내 팝업 */
const passwordChangeCycleDays = computed(
  () => serviceStore.serviceInfo?.passwordChangeCycleDays ?? 90
);

const shouldShowPasswordUpdateAlertModal = ref(false);

const toggleShowPasswordUpdateAlertModal = (show: boolean) => {
  shouldShowPasswordUpdateAlertModal.value = show;
};

const onClickPasswordNextChangeButton = () => {
  toggleShowPasswordUpdateAlertModal(false);
  if (canExtendPasswordExpiration.value) {
    extendPasswordExpiration();
  }
};

const extendPasswordExpiration = async () => {
  const extendResult = await userStore.extendPasswordExpiration();

  if (!extendResult.isSuccess) {
    const { errorMessage, errorTime, resultCode } = extendResult;
    showErrorModal(
      "비밀번호 만료기한 연장 오류",
      errorMessage,
      resultCode,
      errorTime
    );
    return;
  }

  showToastMessage("비밀번호 사용 기한이 연장되었습니다");
};

const handleClickPasswordUpdateOkButton = () => {
  toggleShowPasswordUpdateAlertModal(false);
  goToChangePasswordPage();
};

const goToChangePasswordPage = () => {
  router.push({
    name: RouterName.ChangeExpiredPassword,
    query: { source: ChangePasswordExpiredSourceType.ACTIVE },
  });
};

/** 비밀번호 만료 여부 확인 */
const { checkPasswordExpired } = useCheckPasswordExpired();

const handleCheckPasswordExpired = async () => {
  const result = await checkPasswordExpired(
    passwordExpiredAlramIntervalDay.value
  );

  if (!result?.data) return;

  const { shouldChangePassword, shouldShowChangePasswordAlert } = result.data;

  if (shouldChangePassword) {
    goToChangePasswordPage();
    return;
  }

  if (shouldShowChangePasswordAlert) {
    toggleShowPasswordUpdateAlertModal(true);
    return;
  }
};

provide(PROVIDE_KEY_HANDLE_CHECK_PASSWORD_EXPIRED, handleCheckPasswordExpired);

watch(
  () => deviceStore.applicationIsActive,
  (isActive) => {
    if (!isActive) return;
    handleCheckPasswordExpired();
  }
);

// 확대/축소 처리
const floatingWrapper = ref();
const floatingRightOffsetPx = ref("0px");
const shouldShowZoomControl = computed(() => route.meta.showZoomControl);

const updateFloatingRightOffsetPx = () => {
  const centerContentElement = document.getElementById("center-content");
  if (centerContentElement && floatingWrapper.value) {
    const rect = centerContentElement.getBoundingClientRect();
    // 윈도우의 우측, 하단과 center 요소의 간격 계산
    floatingRightOffsetPx.value = `${window.innerWidth - rect.right}px`;
  } else {
    floatingRightOffsetPx.value = "0px";
  }
};

const zoomControl = ref();

const resetZoom = () => {
  if (!route.meta.isZoomResetRequired) return;
  zoomControl.value?.resetZoom();
};

const addResizeEventListener = () => {
  window.addEventListener("resize", updateFloatingRightOffsetPx);
};

const removeResizeEventListener = () => {
  window.removeEventListener("resize", updateFloatingRightOffsetPx);
};

const onVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    // iOS의 경우 본인인증 화면으로 이동하면 기존 웹 페이지가 unload되지 않고, 백그라운드에 유지되기 때문에 app.vue가 재실행되지 않음
    resizeWebviewWithSafeArea(false);
  }
};

const addVisibilityChangeListener = () => {
  document.addEventListener("visibilitychange", onVisibilityChange);
};

const removeVisibilityChangeListener = () => {
  document.removeEventListener("visibilitychange", onVisibilityChange);
};

const addFocusInListener = () => {
  document.addEventListener("focusin", resetScroll);
};

const removeFocusInListener = () => {
  document.removeEventListener("focusin", resetScroll);
};

const resetScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  if (!target || !target.classList?.contains("wizdome-custom")) return;

  // iOS에서 키보드가 올라오면서 간헐적으로 키보드 높이만큼 컨텐츠가 올라가버리는 현상 방지
  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 200);
};

onMounted(async () => {
  if (!serviceStore.isUpdatedServiceInfo) {
    const success = await getSerivceInfo();
    if (!success) {
      activateServiceStatus();
      router.push({ name: RouterName.PageNotFound });
      return;
    }
  }

  updateSettings();
  await getCampusList();
  activateServiceStatus();
  updateLineInfos();
  getUserInfo();
  updateFloatingRightOffsetPx();
  addResizeEventListener();
  addVisibilityChangeListener();
  if (isiOS) {
    addFocusInListener();
  }
});

onBeforeUnmount(() => {
  removeBoardIntervalBlockChcker();
  removeBookIntervalBlockChcker();
  removeResizeEventListener();
  removeVisibilityChangeListener();
  if (isiOS) {
    removeFocusInListener();
  }
});

const __returned__ = { router, route, isAndroid, isiOS, isNativeApp, isMobile, isReadyForService, updateKey, serviceStore, systemStore, deviceStore, userStore, lineStore, termsList, layoutMode, contentMaxWidth, logoPath, targetPermissions, whiteListDomains, passwordExpiredAlramIntervalDay, canExtendPasswordExpiration, showTermsOnNewWindow, serviceCode, activateServiceStatus, isAndroidFunctionAvailable, firstEnterPage, setStatusBarHeight, setBottomNavBarHeight, setNotificationAllowStatus, updateNotificationAllowStatus, onKeyboardChanged, openLinkWithExternalBrowser, setPushToken, updatePushToken, isNotificationNotDetermined, setNotificationPermissionDetermined, handleAppPermissionsModal, updateIsNotificationDetermined, androidSdkVersion, updateAndroidSdkVersion, updatePermissionSettingsIfNeeded, onAppStatusChanged, getSerivceInfo, updateLineInfos, getUserInfo, getCampusList, resizeWebviewWithSafeArea, setViewportHeight, onChangedWebviewSize, shouldShowLoadingImage, showLoadingImage, removeLoadingImage, currentLocation, getCurrentLocation, setCurrentLocation, isLocationPermissionDenied, updateIsLocationPermissionDenied, hasNoLocationPermission, shouldShowErrorModal, errorTitle, errorMessage, errorCode, errorTime, errorCallback, showErrorModal, hideErrorModal, shouldShowNotice, popupNoticeInfos, isColsedNoticeModal, showNoticeModal, onCloseNoticeModal, shouldShowToast, toastMessage, toastDuration, toastDelay, toastUpdateKey, showToastMessage, shouldShowFinishAppModal, askFinishApp, finishApp, shouldShowAppPermissionsModal, toggleShowAppPermissionsModal, onClosedAppPermissionsModal, removeBoardIntervalBlockChcker, removeBookIntervalBlockChcker, setExternalLinks, updateSettings, passwordChangeCycleDays, shouldShowPasswordUpdateAlertModal, toggleShowPasswordUpdateAlertModal, onClickPasswordNextChangeButton, extendPasswordExpiration, handleClickPasswordUpdateOkButton, goToChangePasswordPage, checkPasswordExpired, handleCheckPasswordExpired, floatingWrapper, floatingRightOffsetPx, shouldShowZoomControl, updateFloatingRightOffsetPx, zoomControl, resetZoom, addResizeEventListener, removeResizeEventListener, onVisibilityChange, addVisibilityChangeListener, removeVisibilityChangeListener, addFocusInListener, removeFocusInListener, resetScroll, ErrorModal, ToastMessage, Modal, NoticeModal, LoadingView, CompanyInfo, AppPermissionsModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})